<script setup lang="ts">
import type { BoardPayload } from '@store'
import { ROUTES } from '@configs'

definePageMeta({
  layout: false,
})

useSetSEO()
useScrollRestoration()

const route = useRoute()
const etcStore = useEtcStore()
const { t, locale } = useI18n()
const filterTabData = computed(() => {
  const data = etcStore.faqFilter.map((item) => ({
    id: item.bbsNttClSn,
    name: item.nttClNm,
  }))
  return [{ id: 0, name: t('board.label.total') }, ...data]
})
const tabId = ref(0)
const keyword = ref('')
const isError = ref(false)
const isLoading = ref(false)
const { gtEvent } = useGoogleTag()

watch(
  () => locale.value,
  () => {
    setTimeout(async () => {
      await etcStore.fetchBoardFilter({ type: 'faq' })
      await fetchFaqs(
        {
          ...etcStore.faqList.payload,
          bbsNttClSn: tabId.value !== 0 ? tabId.value : undefined,
          query: keyword.value,
        },
        true
      )
    })
  }
)

onMounted(async () => {
  await etcStore.fetchBoardFilter({ type: 'faq' })
  const { isChanged, newPayload } = checkEqualFAQsPayload()
  tabId.value = newPayload.bbsNttClSn || 0
  keyword.value = newPayload.query || ''
  if (etcStore.faqList.items.length && !isChanged) return
  await fetchFaqs({ ...etcStore.faqList.payload, ...newPayload }, true)
})

// FAQ 목록 조회 쿼리 비교
const checkEqualFAQsPayload = () => {
  const allowBbsNttClSn = filterTabData.value.map((item) => item.id)
  const newPayload: Partial<BoardPayload> = {
    bbsNttClSn:
      allowBbsNttClSn.find((item) => item === Number(route.query.bbsNttClSn)) ||
      undefined,
    query: route.query.query ? String(route.query.query) : '',
  }
  const isChanged =
    Object.keys(newPayload).filter(
      (key) =>
        newPayload[key as keyof typeof newPayload] !==
        etcStore.faqList.payload[key as keyof typeof newPayload]
    ).length > 0

  return { isChanged, newPayload }
}

// faq 조건 검색
const onSubmit = async (id: number, keyword: string, isSearch?: boolean) => {
  tabId.value = id

  const overridePayload: Partial<BoardPayload> = {
    bbsNttClSn: id || undefined,
    query: keyword.trim() || undefined,
  }

  console.log(overridePayload)

  await fetchFaqs(
    {
      ...etcStore.faqList.payload,
      ...overridePayload,
    },
    true
  )

  await useNavigations({ url: ROUTES.BOARD_FAQ.path, query: overridePayload })

  if (isSearch) {
    gtEvent('userAction', {
      eventCategory: '사용자',
      eventAction: '자주 묻는 질문 > 검색',
      eventLabel: t('board.title.faq'),
      eventSlot: '자주 묻는 질문 > 검색 버튼',
      eventI18nAddr: useKoreanTranslation('board.title.faq'),
      eventComponent: 'Button',
      pageNum: etcStore.faqList.payload.pageNum,
      pageSize: etcStore.faqList.payload.pageSize,
      type: etcStore.faqList.payload.type,
      keyword,
    })
  }
}

// faq 목록 조회
const fetchFaqs = async (
  payload = etcStore.faqList.payload,
  refresh = false
) => {
  try {
    isLoading.value = true
    isError.value = false
    await etcStore.fetchBoardList(payload, refresh)
  } catch {
    isError.value = true
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <NuxtLayout name="board">
    <template #leftSideBar>
      <NovaBoxBoardSideMenu :active="'faq'" />
    </template>

    <ClientOnly>
      <div class="board-wrapper">
        <div class="list-area">
          <div class="top-header">
            <span class="title">{{ $t('board.title.faq') }}</span>
            <div class="search-input">
              <NovaInput
                v-model="keyword"
                :placeholder="$t('board.label.search')"
                :theme="'bgwhite'"
                :show-max="false"
                @on-search="() => onSubmit(tabId, keyword, true)"
                @on-clear="() => onSubmit(tabId, keyword)"
              >
                <template v-if="keyword" #tools>
                  <NovaButtonIcon
                    :icon="{ type: 'outline', name: 'search' }"
                    class="search-button"
                    @click="() => onSubmit(tabId, keyword, true)"
                  />
                </template>
              </NovaInput>
            </div>
          </div>
          <div v-show="filterTabData.length" class="tab-area">
            <ClientOnly>
              <NovaTab
                :tab-value="filterTabData"
                :init-tab="tabId"
                :size="40"
                :theme="'black'"
                @on-change-tab="(value: any) => onSubmit(value.id, keyword)"
              />
            </ClientOnly>
          </div>
          <div class="list-box">
            <NovaBoxEmptyMessage
              v-show="
                etcStore.faqList.refresh &&
                !etcStore.faqList.items.length &&
                !isError
              "
              :message="$t('statusMessages.boardList.empty')"
              :fill="true"
            />
            <NovaBoxEmptyMessage
              v-show="isError && !etcStore.faqList.items.length"
              :message="$t('statusMessages.boardList.error')"
              :fill="true"
            />
            <NovaLoadingIndicator
              v-show="etcStore.faqList.refresh && isLoading"
              :size="80"
              :fill="true"
              :bg-bright="'light'"
            />

            <NovaRecyclerGrid
              v-if="etcStore.faqList.items.length"
              :key="`${keyword}-${tabId}`"
              :items="etcStore.faqList.items"
              :is-last="etcStore.faqList.last"
              :is-loading="isLoading"
              :is-error="isError"
              :is-refresh="etcStore.faqList.refresh"
              :key-name="'nttGroupId'"
              :page-mode="true"
              :responsive="{
                mobile: {
                  columns: 1,
                  rowGap: 12,
                  columnGap: 12,
                  padding: { top: 15 },
                },
                tablet: {
                  columns: 1,
                  rowGap: 12,
                  columnGap: 12,
                  padding: { top: 30 },
                },
                desktop: {
                  columns: 1,
                  rowGap: 12,
                  columnGap: 12,
                  padding: { top: 30 },
                },
              }"
              @load-more="fetchFaqs"
            >
              <template #item="{ item }">
                <NovaBoxBoardListFaqItem :source="item" />
              </template>
            </NovaRecyclerGrid>
          </div>
        </div>
      </div>
    </ClientOnly>
  </NuxtLayout>
</template>

<style lang="scss" scoped>
.board-wrapper,
.list-area,
.list-box,
.list {
  height: 100%;
  overflow: hidden;
}

.board-wrapper {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 40px;
  padding-top: 20px;
  color: $color-text-2;
}

.list-area {
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  .top-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .title {
      @include text-style($text-heading2-bold);
      line-height: 40px;
    }

    .search-input {
      width: 300px;

      .search-button {
        color: $color-text-4;
      }
    }
  }
}

.tab-area {
  margin-top: 30px;
  padding: 20px;
  background-color: $color-white;
  border-radius: $radius-10;
}

.list {
  padding-top: 30px;
}

@include mobile {
  .board-wrapper {
    padding: 15px 20px;
    flex-direction: column;
    gap: 20px;
  }

  .list-area {
    width: 100%;

    .top-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 15px;

      .search-input {
        width: 100%;
      }
    }
  }

  .tab-area {
    background-color: transparent;
    padding: 0;
    margin-top: 15px;

    :deep(.tabs-container .tabs.theme-board .swiper .tab) {
      &.on {
        .tab-button {
          background-color: $color-primary-orange;
        }
      }
      .tab-button {
        background-color: $color-white;
      }
    }
  }
  .list {
    padding-top: 15px;
  }
}
</style>
